import React, { useEffect, useState } from 'react';
import Markdown from 'markdown-to-jsx';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

const copyToClipboard = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) throw new Error('Clipboard copy was not available');
};

const safeHref = (href = '') => {
  try {
    const parsed = new URL(href, window.location.origin);
    return ['http:', 'https:', 'mailto:'].includes(parsed.protocol) ? href : '#';
  } catch (error) {
    return '#';
  }
};

const MarkdownLink = ({ href, ...props }) => {
  const safe = safeHref(href);
  const external = safe.startsWith('http://') || safe.startsWith('https://');
  return (
    <Link
      {...props}
      href={safe}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      sx={{ overflowWrap: 'anywhere' }}
    />
  );
};

const MarkdownTable = (props) => (
  <TableContainer component={Paper} variant="outlined" sx={{ my: 2, maxWidth: '100%', overflowX: 'auto' }}>
    <Table size="small" {...props} />
  </TableContainer>
);

const MarkdownImageReference = ({ src, alt }) => (
  <Link href={safeHref(src)} target="_blank" rel="noopener noreferrer">
    {alt || 'Open referenced image'}
  </Link>
);

const markdownOverrides = {
  h1: {
    component: (props) => (
      <Typography component="h1" variant="h4" sx={{ mt: 2.5, mb: 1.25 }} {...props} />
    ),
  },
  h2: {
    component: (props) => (
      <Typography component="h2" variant="h5" sx={{ mt: 2.5, mb: 1 }} {...props} />
    ),
  },
  h3: {
    component: (props) => (
      <Typography component="h3" variant="h6" sx={{ mt: 2, mb: 0.75 }} {...props} />
    ),
  },
  h4: {
    component: (props) => (
      <Typography component="h4" variant="subtitle1" sx={{ mt: 1.75, mb: 0.5, fontWeight: 700 }} {...props} />
    ),
  },
  p: {
    component: (props) => (
      <Typography component="p" variant="body1" sx={{ my: 1.1, lineHeight: 1.7 }} {...props} />
    ),
  },
  ul: { component: (props) => <Box component="ul" sx={{ my: 1, pl: 3.5 }} {...props} /> },
  ol: { component: (props) => <Box component="ol" sx={{ my: 1, pl: 3.5 }} {...props} /> },
  li: { component: (props) => <Box component="li" sx={{ mb: 0.5, lineHeight: 1.65 }} {...props} /> },
  blockquote: {
    component: (props) => (
      <Box
        component="blockquote"
        sx={{ my: 1.5, mx: 0, pl: 2, py: 0.25, borderLeft: 4, borderColor: 'primary.light', color: 'text.secondary' }}
        {...props}
      />
    ),
  },
  a: { component: MarkdownLink },
  table: { component: MarkdownTable },
  thead: { component: (props) => <TableHead sx={{ bgcolor: 'action.hover' }} {...props} /> },
  tbody: { component: (props) => <TableBody {...props} /> },
  tr: { component: (props) => <TableRow {...props} /> },
  th: {
    component: (props) => (
      <TableCell component="th" sx={{ fontWeight: 700, verticalAlign: 'top', whiteSpace: 'nowrap' }} {...props} />
    ),
  },
  td: {
    component: (props) => (
      <TableCell sx={{ verticalAlign: 'top', minWidth: 110, overflowWrap: 'anywhere' }} {...props} />
    ),
  },
  pre: {
    component: (props) => (
      <Box
        component="pre"
        sx={{ my: 1.5, p: 1.5, overflowX: 'auto', borderRadius: 1, bgcolor: 'grey.900', color: 'grey.100', fontSize: '0.85rem' }}
        {...props}
      />
    ),
  },
  code: {
    component: (props) => (
      <Box
        component="code"
        sx={{ px: 0.4, py: 0.15, borderRadius: 0.5, bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: '0.9em' }}
        {...props}
      />
    ),
  },
  hr: { component: () => <Divider sx={{ my: 2.5 }} /> },
  img: { component: MarkdownImageReference },
  script: { component: () => null },
  iframe: { component: () => null },
  object: { component: () => null },
  style: { component: () => null },
};

function AgentResponseDialog({ open, content = '', onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [copyState, setCopyState] = useState('idle');

  useEffect(() => {
    setCopyState('idle');
  }, [open, content]);

  const handleCopy = async () => {
    try {
      await copyToClipboard(content);
      setCopyState('copied');
    } catch (error) {
      setCopyState('failed');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby="agent-response-dialog-title"
      PaperProps={{ sx: { height: fullScreen ? '100%' : 'min(88vh, 900px)' } }}
    >
      <DialogTitle
        component="div"
        sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography id="agent-response-dialog-title" component="h2" variant="h6" sx={{ flex: 1 }}>
            Response details
          </Typography>
          <Button
            size="small"
            onClick={handleCopy}
            startIcon={copyState === 'copied' ? <CheckIcon /> : <CopyIcon />}
            aria-label="Copy complete response"
          >
            {copyState === 'copied' ? 'Copied' : 'Copy'}
          </Button>
          <IconButton onClick={onClose} aria-label="Close response details">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto', px: { xs: 2, sm: 4 }, py: 2 }}>
        <Box
          data-testid="formatted-agent-response"
          sx={{ maxWidth: 900, mx: 'auto', '& > :first-of-type': { mt: 0 }, '& > :last-child': { mb: 0 } }}
        >
          <Markdown
            options={{
              disableParsingRawHTML: true,
              forceBlock: true,
              overrides: markdownOverrides,
            }}
          >
            {content}
          </Markdown>
        </Box>
      </DialogContent>
      <Snackbar
        open={copyState === 'failed'}
        autoHideDuration={4000}
        onClose={() => setCopyState('idle')}
        message="Could not access the clipboard. Please select the response text manually."
      />
    </Dialog>
  );
}

export default AgentResponseDialog;
